<template>
  <div>
    <div
      class="fm-form-input"
      :class="{
        'mt-2': !!label,
        'bg-white': !disabled,
        'bg-gray-100': disabled,
        [selectClass]: !!selectClass
      }"
    >
      <label v-if="label" :class="labelClass">
        {{ label }}
        <span v-if="label && required" class="text-red-600">*</span>
      </label>
      <Combobox v-model="localModelValue" multiple :disabled="disabled">
        <Float
          adaptive-width
          strategy="fixed"
          placement="bottom-start"
          :flip="true"
          :shift="true"
          :offset="1"
          @leave="query = ''"
        >
          <div
            class="flex w-full cursor-default !flex-col overflow-hidden rounded-lg text-left"
            :class="{
              '!bg-white': !disabled,
              'bg-gray-100': disabled
            }"
          >
            <ul
              v-if="localModelValue.length > 0"
              class="relative flex w-full flex-wrap gap-2 pt-3 pr-9 pl-2"
            >
              <li
                v-for="(item, idx) in localModelValue"
                :key="item"
                class="bg-primary-300 flex flex-row items-center gap-1 overflow-hidden rounded px-1 text-white"
              >
                <div class="break-words whitespace-normal">
                  {{ chipLabel(item) }}
                </div>
                <button
                  class="flex items-center"
                  type="button"
                  tabindex="-1"
                  :disabled="disabled"
                  @click="remove(idx)"
                >
                  <FontAwesomeIcon :icon="['far', 'times']" size="sm" />
                </button>
              </li>
            </ul>
            <ComboboxInput
              autocomplete="nope"
              autofill="nope"
              class="w-full border-none py-2 pr-10 pl-3 text-sm leading-5 text-gray-900 focus:ring-0"
              @change="query = $event.target.value"
            />
            <ComboboxButton
              class="absolute inset-y-0 right-0 flex items-center pr-2"
              :data-testid="`select-${name}`"
            >
              <FontAwesomeIcon :icon="['fal', 'chevron-down']" />
            </ComboboxButton>
          </div>
          <ComboboxOptions
            class="max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm"
          >
            <div
              v-if="hasNoResults"
              class="relative cursor-default px-4 py-2 text-gray-700 select-none"
            >
              Nothing found.
            </div>

            <template v-if="isGrouped">
              <ComboboxOption
                v-for="item in filteredOptions.ungrouped"
                :key="item[trackBy]"
                v-slot="{ selected, active, disabled }"
                as="template"
                :value="item[trackBy]"
              >
                <li
                  class="relative cursor-default py-2 pr-4 pl-8 select-none"
                  :class="{
                    'bg-gray-200 text-gray-700': disabled,
                    'bg-primary-500 text-contrast-primary': selected,
                    'bg-primary-300 text-white': active,
                    'text-gray-900': !active && !selected
                  }"
                >
                  <slot
                    name="option"
                    :option="item"
                    :active="active"
                    :selected="selected"
                  >
                    <span
                      class="flex w-full flex-row items-center gap-1.5 break-words whitespace-normal"
                    >
                      <span
                        class="flex-1"
                        :class="[
                          selected ? 'font-medium' : 'font-normal',
                          capitalize ? 'capitalize-first' : ''
                        ]"
                      >
                        {{ item[displayKey] }}
                      </span>
                    </span>
                  </slot>
                  <span
                    v-if="selected"
                    class="absolute inset-y-0 left-0 flex items-center pl-3"
                  >
                    <FontAwesomeIcon :icon="['fas', 'check']" />
                  </span>
                </li>
              </ComboboxOption>

              <template v-for="group in filteredOptions.groups" :key="group.group">
                <div
                  class="bg-primary-200 text-primary capitalize-first flex cursor-pointer flex-row items-center justify-between px-4 py-2 text-sm font-bold"
                  @click="toggleGroup(group.groupOption)"
                >
                  <span>{{ group.group }}</span>
                  <FontAwesomeIcon
                    v-if="isGroupSelected(group.groupOption)"
                    :icon="['fas', 'check']"
                  />
                </div>
                <ComboboxOption
                  v-for="item in group.options"
                  :key="item[trackBy]"
                  v-slot="{ selected, active, disabled }"
                  as="template"
                  :value="item[trackBy]"
                >
                  <li
                    class="relative cursor-default py-2 pr-4 pl-8 select-none"
                    :class="{
                      'bg-gray-200 text-gray-700': disabled,
                      'bg-primary-500 text-contrast-primary': selected,
                      'bg-primary-300 text-white': active,
                      'text-gray-900': !active && !selected
                    }"
                  >
                    <slot
                      name="option"
                      :option="item"
                      :active="active"
                      :selected="selected"
                    >
                      <span
                        class="flex w-full flex-row items-center gap-1.5 break-words whitespace-normal"
                      >
                        <span
                          class="flex-1"
                          :class="[
                            selected ? 'font-medium' : 'font-normal',
                            capitalize ? 'capitalize-first' : ''
                          ]"
                        >
                          {{ item[displayKey] }}
                        </span>
                      </span>
                    </slot>
                    <span
                      v-if="selected"
                      class="absolute inset-y-0 left-0 flex items-center pl-3"
                    >
                      <FontAwesomeIcon :icon="['fas', 'check']" />
                    </span>
                  </li>
                </ComboboxOption>
              </template>
            </template>

            <template v-else>
              <ComboboxOption
                v-for="item in filteredOptions"
                :key="item[trackBy]"
                v-slot="{ selected, active, disabled }"
                as="template"
                :value="item[trackBy]"
              >
                <li
                  class="relative cursor-default py-2 pr-4 pl-8 select-none"
                  :class="{
                    'bg-gray-200 text-gray-700': disabled,
                    'bg-primary-500 text-contrast-primary': selected,
                    'bg-primary-300 text-white': active,
                    'text-gray-900': !active && !selected
                  }"
                >
                  <slot
                    name="option"
                    :option="item"
                    :active="active"
                    :selected="selected"
                  >
                    <span
                      class="flex w-full flex-row items-center gap-1.5 break-words whitespace-normal"
                    >
                      <span v-if="colored" class="shrink-0">
                        <span
                          :data-tint="item.bg_color"
                          class="bg-primary block h-3 w-3 rounded-full"
                        ></span>
                      </span>
                      <span
                        class="flex-1"
                        :class="[
                          selected ? 'font-medium' : 'font-normal',
                          capitalize ? 'capitalize-first' : ''
                        ]"
                      >
                        {{ item[displayKey] }}
                      </span>
                    </span>
                  </slot>
                  <span
                    v-if="selected"
                    class="absolute inset-y-0 left-0 flex items-center pl-3"
                  >
                    <FontAwesomeIcon :icon="['fas', 'check']" />
                  </span>
                </li>
              </ComboboxOption>
            </template>
          </ComboboxOptions>
        </Float>
      </Combobox>
    </div>
    <ErrorContainer :error-key="name" :errors="errors" />
  </div>
</template>

<script>
import {
  Combobox,
  ComboboxInput,
  ComboboxButton,
  ComboboxOptions,
  ComboboxOption
} from '@headlessui/vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import ErrorContainer from '@c/addf-package/components/BaseShowEditInput/ErrorContainer.vue'
import { Float } from '@headlessui-float/vue'
import { groupOptions, chipLabel } from '@c/BaseFormComponent/selectGrouping'

export default {
  name: 'MultiSelectSearchableComponent',
  components: {
    Float,
    ErrorContainer,
    ComboboxOption,
    ComboboxOptions,
    ComboboxButton,
    ComboboxInput,
    Combobox,
    FontAwesomeIcon
  },
  props: {
    modelValue: {
      type: Array,
      required: true
    },
    size: {
      type: String,
      required: false,
      default: 'normal'
    },
    values: {
      type: Array,
      required: true
    },
    required: {
      type: Boolean,
      required: false,
      default: false
    },
    trackBy: {
      type: String,
      required: false,
      default: 'id'
    },
    displayKey: {
      type: String,
      required: false,
      default: 'name'
    },
    label: {
      type: String,
      required: false,
      default: null
    },
    selectedLabel: {
      type: String,
      required: false,
      default: null
    },
    noneSelectedLabel: {
      type: String,
      required: false,
      default: null
    },
    numberDisplayed: {
      type: Number,
      required: false,
      default: 1
    },
    includeSelectAllOption: {
      type: Boolean,
      required: false,
      default: true
    },
    favorite: {
      type: Array,
      required: false,
      default: null
    },
    favoriteOnTop: {
      type: Boolean,
      required: false,
      default: false
    },
    name: {
      type: String,
      required: true
    },
    errors: {
      type: Array,
      required: false,
      default: () => []
    },
    colored: {
      type: Boolean,
      required: false,
      default: false
    },
    capitalize: {
      type: Boolean,
      required: false,
      default: true
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false
    },
    isGrouped: {
      type: Boolean,
      required: false,
      default: false
    },
    groupKey: {
      type: String,
      required: false,
      default: 'group'
    },
    labelClass: {
      type: String,
      required: false,
      default: ''
    },
    selectClass: {
      type: String,
      required: false,
      default: ''
    }
  },
  emits: ['update:modelValue'],
  data() {
    return {
      query: ''
    }
  },
  computed: {
    localModelValue: {
      get() {
        return this.modelValue
      },
      set(value) {
        this.$emit('update:modelValue', value)
      }
    },
    filteredOptions() {
      const baseValues =
        this.query === ''
          ? this.orderedValues
          : this.orderedValues.filter((value) => {
              return this.$h
                .normalizeSearch(value[this.displayKey])
                .includes(this.$h.normalizeSearch(this.query))
            })

      if (!this.isGrouped) {
        return baseValues
      }

      return groupOptions(baseValues, this.groupKey)
    },
    hasNoResults() {
      if (this.query === '') {
        return false
      }
      if (!this.isGrouped) {
        return this.filteredOptions.length === 0
      }
      return (
        this.filteredOptions.ungrouped.length === 0 &&
        this.filteredOptions.groups.length === 0
      )
    },
    orderedValues() {
      if (
        this.favorite === null ||
        !Array.isArray(this.favorite) ||
        this.favorite.length === 0 ||
        !this.favoriteOnTop
      ) {
        return this.values
      }
      return this.values.sort((a, b) =>
        this.favorite.includes(a.id) ? -1 : this.favorite.includes(b.id) ? 1 : 0
      )
    },
    selectedValuesLabel() {
      if (!this.selectedValues) {
        return this.noneSelectedLabel ?? this.$t('global.aucun_selectionne')
      }
      let lSelected = this.selectedValues.length
      if (lSelected > this.numberDisplayed) {
        return (
          lSelected +
          ' ' +
          (this.selectedLabel ?? this.$t('global.selectionnes'))
        )
      } else if (lSelected === 0) {
        return this.noneSelectedLabel ?? this.$t('global.aucun_selectionne')
      } else if (
        lSelected <= this.numberDisplayed &&
        this.selectedValues.length > 0
      ) {
        return this.values
          .filter((value) => this.modelValue.includes(value[this.trackBy]))
          .map((value) => value[this.displayKey])
          .join(', ')
      }
    },
    sizeClass() {
      switch (this.size) {
        case 'small':
          return 'py-2'
        case 'normal':
          return 'py-2'
      }
    },
    listboxOptionClass() {
      switch (this.size) {
        case 'small':
          return ''
        case 'normal':
          return 'mt-4'
      }
    }
  },
  methods: {
    selectAll() {
      this.selectedValues = this.values.map((value) => value[this.trackBy])
    },
    unselectAll() {
      this.selectedValues = []
    },
    remove(index) {
      const newValue = this.localModelValue.filter((_, i) => i !== index)
      this.localModelValue = newValue
    },
    toggleGroup(groupOption) {
      if (!groupOption) {
        return
      }
      const value = groupOption[this.trackBy]
      this.localModelValue = this.localModelValue.includes(value)
        ? this.localModelValue.filter((v) => v !== value)
        : [...this.localModelValue, value]
    },
    isGroupSelected(groupOption) {
      return !!groupOption && this.localModelValue.includes(groupOption[this.trackBy])
    },
    chipLabel(itemId) {
      const item = this.values.find((value) => value[this.trackBy] === itemId)
      return chipLabel(item, {
        displayKey: this.displayKey,
        groupKey: this.groupKey,
        isGrouped: this.isGrouped
      })
    }
  }
}
</script>

<style scoped></style>
